/**
 * The Ledger tab's four sections (dashboard spec §2.2 tab 7, W4 card C4): Finances, Standing, Law, Corporation.
 * Read-only bodies over `model.ts`; the docked-only actions (pay lease, pay fines, found a corporation) are shown
 * disabled with `DockedAction` (spec §2.1, §2.3). Each body re-reads at `refreshHz` (4 Hz) only while the dashboard
 * is open; the async worker reads (`captainView`, `corpView`) at ledger.json `asyncHz`. The shell keeps bodies mounted while
 * closed, so the timers check `isOpen()` before reading.
 */
import { useEffect, useState, type ReactElement } from 'react'
import type { Career } from '../../../../career/types'
import type { SimFactionId } from '../../../../data/m2Schemas'
import type { CaptainView, CorpView, FactionId, RelationsView } from '../../../../strategic/types'
import type { DashboardConfig } from '../../config'
import { dockedActionModel, toNearestStation } from '../../data'
import type { StationDirectory } from '../../data/types'
import { DockedAction } from '../../parts'
import type { DashContext, DashSection, DashSectionProps } from '../../registry'
import { loadLedgerConfig, type LedgerConfig } from './config'
import { corpModel, createStandingTracker, financesModel, lawModel, standingModel, type LawClaim, type LedgerRow } from './model'
import './ledger.css'

export interface LedgerDeps {
  readonly dash: DashboardConfig
  career(): Career | null
  relations(): RelationsView | null
  /** The current system's claim (undefined = not in a system). */
  claim(): LawClaim | null | undefined
  captains(): Promise<CaptainView> | null
  corp(): Promise<CorpView> | null
  /** The known-station directory for Set course (null outside the Reach). */
  directory(): StationDirectory | null
  setCourse(dir: StationDirectory, stationId: string): void
  systemName(id: string): string
  factionName(f: FactionId): string
  simFactionName(f: SimFactionId): string
  isOutlaw(f: FactionId): boolean
  /** law.json `fines.mult`, `fines.rangeM` (formatted). */
  readonly finesMult: number
  readonly finesRange: string
  isOpen(): boolean
}

function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m)
}

/** Re-renders at `hz` while the dashboard is open. */
function useTick(hz: number, isOpen: () => boolean): void {
  const [, setN] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => {
      if (isOpen()) setN((n) => (n + 1) % 1_000_000)
    }, 1000 / hz)
    return (): void => window.clearInterval(id)
  }, [hz, isOpen])
}

/** The latest value of an async worker read, re-asked at `hz` while open. */
function useAsync<T>(read: () => Promise<T> | null, hz: number, isOpen: () => boolean): T | null {
  const [v, setV] = useState<T | null>(null)
  useEffect(() => {
    let live = true
    const ask = (): void => {
      if (!isOpen()) return
      read()
        ?.then((x) => {
          if (live) setV(x)
        })
        .catch(() => undefined)
    }
    ask()
    const id = window.setInterval(ask, 1000 / hz)
    return (): void => {
      live = false
      window.clearInterval(id)
    }
  }, [read, hz, isOpen])
  return v
}

function Rows({ rows }: { rows: readonly LedgerRow[] }): ReactElement {
  return (
    <dl className="ledger-rows">
      {rows.map((r) => (
        <div key={r.label} className={`ledger-row ledger-tone--${r.tone}`}>
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function NoCareer({ cfg }: { cfg: LedgerConfig }): ReactElement {
  return <p className="dash-empty__line">{cfg.noCareer}</p>
}

function DockedOnly(p: { deps: LedgerDeps; label: string; reason: string; service?: 'lease-office'; where?: string }): ReactElement {
  const text = p.deps.dash.dockedAction
  const dir = p.deps.directory()
  if (p.service !== undefined && dir !== null) {
    const m = dockedActionModel(text, dir, p.service)
    const hit = m.hit
    return (
      <DockedAction
        cfg={text}
        label={p.label}
        reason={p.reason}
        nearest={toNearestStation(text, hit)}
        onSetCourse={() => {
          if (hit !== null) p.deps.setCourse(dir, hit.station.id)
        }}
      />
    )
  }
  // No station service names a Registry or the Charter Hub: say where instead of a nearest station.
  return <DockedAction cfg={{ ...text, none: p.where ?? text.none }} label={p.label} reason={p.reason} nearest={null} onSetCourse={() => undefined} />
}

export function createLedgerSections(deps: LedgerDeps): DashSection[] {
  const cfg = loadLedgerConfig()
  const tracker = createStandingTracker()
  const hz = deps.dash.refreshHz
  const isOpen = deps.isOpen
  const readCaptains = (): Promise<CaptainView> | null => deps.captains()
  const readCorp = (): Promise<CorpView> | null => deps.corp()

  function Finances({ ctx }: DashSectionProps): ReactElement {
    useTick(hz, isOpen)
    const captains = useAsync(readCaptains, cfg.asyncHz, isOpen)
    const career = deps.career()
    if (career === null) return <NoCareer cfg={cfg} />
    const m = financesModel(cfg, career, captains)
    const t = cfg.finances
    return (
      <div className="ledger" data-ledger="finances">
        <Rows rows={m.rows} />
        {m.leaseOpen && !ctx.docked ? <DockedOnly deps={deps} label={t.payLease} reason={deps.dash.dockedAction.reasons['lease-office']} service="lease-office" /> : null}
        <h3 className="ledger-h">{t.history}</h3>
        {m.history.length === 0 ? (
          <p className="ledger-dim">{t.historyEmpty}</p>
        ) : (
          <ul className="ledger-list">
            {m.history.map((h, i) => (
              <li key={i} className="ledger-row">
                <span>{h.label}</span> <span className="ledger-num">{h.amount}</span> <span className="ledger-dim">{h.ago}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  function Standing(): ReactElement {
    useTick(hz, isOpen)
    const career = deps.career()
    if (career === null) return <NoCareer cfg={cfg} />
    tracker.observe(career.standings, career.gameTime)
    const rows = standingModel(cfg, career, tracker, deps.simFactionName)
    const c = cfg.standing.columns
    return (
      <table className="ledger ledger-table" data-ledger="standing">
        <thead>
          <tr>
            <th>{c.faction}</th>
            <th>{c.value}</th>
            <th>{c.tier}</th>
            <th>{c.last}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-faction={r.id} className={`ledger-tone--${r.tone}`}>
              <td>{r.name}</td>
              <td className="ledger-num">
                {r.value} <span aria-hidden="true">{r.trend === 'up' ? '▲' : r.trend === 'down' ? '▼' : '·'}</span>
              </td>
              <td>{r.tier}</td>
              <td className="ledger-dim">{r.last}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  function Law({ ctx }: DashSectionProps): ReactElement {
    useTick(hz, isOpen)
    const t = cfg.law
    const m = lawModel(cfg, { claim: deps.claim(), relations: deps.relations(), finesMult: deps.finesMult, isOutlaw: deps.isOutlaw, factionName: deps.factionName })
    return (
      <div className="ledger" data-ledger="law">
        <Rows rows={[m.jurisdiction, m.status, { label: t.finesOwed, value: m.finesText, tone: m.finesOwed > 0 ? 'warn' : 'neutral' }]} />
        {m.bounties.length === 0 ? (
          <p className="ledger-dim">{t.noBounties}</p>
        ) : (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>{t.columns.authority}</th>
                <th>{t.columns.bounty}</th>
                <th>{t.columns.wanted}</th>
              </tr>
            </thead>
            <tbody>
              {m.bounties.map((b) => (
                <tr key={b.id}>
                  <td>{b.name}</td>
                  <td className="ledger-num">{b.bounty}</td>
                  <td>{b.wanted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {m.finesOwed > 0 && !ctx.docked ? <DockedOnly deps={deps} label={t.payFines} reason={t.payFinesReason} where={fill(t.payFinesWhere, { range: deps.finesRange })} /> : null}
      </div>
    )
  }

  function Corporation({ ctx }: DashSectionProps): ReactElement {
    const corp = useAsync(readCorp, cfg.asyncHz, isOpen)
    const m = corpModel(cfg, corp, deps.systemName)
    const t = cfg.corporation
    if (m.state === 'loading') return <p className="dash-empty__line">{m.text}</p>
    if (m.state === 'none') {
      return (
        <div className="ledger" data-ledger="corporation">
          <p className="dash-empty__line">{m.text}</p>
          <p className="ledger-dim">{m.needs}</p>
          {!ctx.docked ? <DockedOnly deps={deps} label={t.found} reason={t.foundReason} where={t.foundWhere} /> : null}
        </div>
      )
    }
    return (
      <div className="ledger" data-ledger="corporation">
        <h3 className="ledger-h">{m.title}</h3>
        <Rows rows={m.rows} />
        <h3 className="ledger-h">{t.income}</h3>
        <Rows rows={m.income} />
        <h3 className="ledger-h">{t.costs}</h3>
        <Rows rows={m.costs} />
        <h3 className="ledger-h">{t.claims}</h3>
        {m.claims.length === 0 ? <p className="ledger-dim">{t.noClaims}</p> : <Rows rows={m.claims} />}
        <h3 className="ledger-h">{t.milestones}</h3>
        <Rows rows={m.milestones} />
      </div>
    )
  }

  const career = (c: DashContext): boolean => c.career
  return [
    { id: 'ledger.finances', tab: 'ledger', order: cfg.order.finances, label: cfg.sections.finances, available: career, component: Finances },
    { id: 'ledger.standing', tab: 'ledger', order: cfg.order.standing, label: cfg.sections.standing, available: career, component: Standing },
    { id: 'ledger.law', tab: 'ledger', order: cfg.order.law, label: cfg.sections.law, available: career, component: Law },
    { id: 'ledger.corporation', tab: 'ledger', order: cfg.order.corporation, label: cfg.sections.corporation, available: career, component: Corporation },
  ]
}
